import { GridColDef } from "@biggeo/bg-ui";
import {
    CellItem,
    ColFilterType,
    DataGridContainer,
    Grid,
    IconButton,
    Typography,
    useDataGridOptions,
} from "@biggeo/bg-ui/lab";
import { CropFreeOutline, OpenInNewOutline } from "@biggeo/bg-ui/lab/icons";

import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { area } from "@turf/turf";
import { pipe } from "fp-ts/lib/function";
import concat from "lodash/concat";
import isEqual from "lodash/isEqual";
import lowerCase from "lodash/lowerCase";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../navigation/redux/model";
import { convertSqmToSkm } from "../../utils/utils";
import {
    GeometryCollection,
    SavedArea,
    useFetchAllSavedAreasQuery,
} from "@biggeo/bg-server-lib/studio";

interface ISavedAreasTableContainer {
    areaId: string;
    mapTemplateId?: string;
    selectedAreaName?: string;
}

const SavedAreasTableContainer = ({
    areaId,
    mapTemplateId,
    selectedAreaName,
}: Readonly<ISavedAreasTableContainer>) => {
    const toPage = useNavigate();

    const {
        dataGridFetchInputProps: { sortModel, ...rest },
        filterSearchPaginateProps,
    } = useDataGridOptions();

    const {
        queryReturn: { loading: savedAreasLoading, data },
    } = useFetchAllSavedAreasQuery({
        variables: {
            input: {
                fkAreaId: areaId,
                fkMapTemplateId: mapTemplateId,
                options: rest,
                isSavedViewArea: false,
            },
        },
    });

    const calculateTotalArea = (geometries: GeometryCollection) => {
        return geometries.geometries
            .reduce(
                (acc, val) =>
                    pipe(
                        {
                            type: MapboxDraw.constants.geojsonTypes.POLYGON,
                            coordinates:
                                val.coordinates as GeoJSON.Position[][],
                        },
                        area,
                        convertSqmToSkm,
                        (sq) => acc + sq
                    ),
                0
            )
            .toFixed(4);
    };

    const cols: ReadonlyArray<GridColDef<SavedArea>> = [
        {
            field: "src",
            headerName: "Src",
            flex: 1,
            width: 200,
            minWidth: 75,
            sortable: false,
            renderCell: () => (
                <Grid
                    container
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={8}
                    sx={{
                        padding: 2,
                    }}
                >
                    <IconButton
                        readonly
                        variant="tonal"
                        density="dense"
                        color="primary"
                    >
                        <CropFreeOutline size="sm" />
                    </IconButton>
                </Grid>
            ),
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 200,
            sortable: false,
            type: ColFilterType.string,
            filterable: true,
        },
        {
            field: "area",
            flex: 1,
            minWidth: 175,
            sortable: false,
            type: ColFilterType.number,
            renderHeader: () => (
                <CellItem
                    sx={{
                        color: (theme) => theme.palette.disabled.onContainer,
                    }}
                    title="Area"
                />
            ),
            renderCell: (params) => (
                <Typography
                    variant="body3"
                    fontWeight="semibold"
                    sx={{ padding: 4 }}
                >
                    {`${calculateTotalArea(params.row.geometries)} km`}
                    <sup>2</sup>
                </Typography>
            ),
        },
        {
            field: "view",
            headerName: "View",
            renderHeader: (params) => (
                <CellItem
                    sx={{
                        color: (theme) => theme.palette.disabled.onContainer,
                    }}
                    title={params.colDef.headerName}
                />
            ),
            renderCell: (params) => (
                <Grid container justifyContent="center">
                    <IconButton
                        variant="filled"
                        density="dense"
                        disabled={!params.row.mapUse}
                        sx={{ flexShrink: 0 }}
                        onClick={() => {
                            toPage(
                                `${Routes.mapView}/${mapTemplateId}/map?mapTab=boundaries&savedAreaId=${params.row.id}`
                            );
                        }}
                    >
                        <Typography variant={"button"} fontWeight={"semibold"}>
                            Open
                        </Typography>
                        <OpenInNewOutline size="sm" />
                    </IconButton>
                </Grid>
            ),
        },
    ];

    const manageColData: GridColDef<SavedArea> = {
        field: "actions",
        minWidth: 125,
        sortable: false,
        filterable: false,
        headerName: "",
        type: "actions",
        renderCell: (params) => (
            <Grid
                container
                justifyContent="center"
                sx={{ paddingLeft: 4, paddingRight: 4 }}
            >
                <IconButton
                    variant="outlined"
                    density="comfortable"
                    sx={{ flexShrink: 0 }}
                    onClick={() => {
                        toPage(
                            `${Routes.mapView}/${mapTemplateId}/configuration/${params.row.id}`
                        );
                    }}
                >
                    <Typography variant={"button"} fontWeight={"semibold"}>
                        Manage
                    </Typography>
                </IconButton>
            </Grid>
        ),
    };

    return (
        <DataGridContainer
            columnVisibilityModel={{
                id: false,
            }}
            disableColumnResize
            loading={savedAreasLoading}
            columns={
                isEqual(lowerCase(selectedAreaName), "custom boundaries")
                    ? concat(cols, manageColData)
                    : cols
            }
            initialState={{ pinnedColumns: { right: ["actions"] } }}
            rows={data?.fetchAllSavedAreas || []}
            rowCount={data?.fetchAllSavedAreas.length}
            filterSearchPaginateProps={filterSearchPaginateProps}
            title={selectedAreaName}
        />
    );
};

export default SavedAreasTableContainer;
